// Per-stop admin edit form (D3g UI). Sibling to LogisticsRoutesAddStopForm:
// extracted as its own file so LogisticsRoutesDetailCard stays under the
// soft-warning band (the form is intentionally larger than the add form
// because it exposes the completion timestamps + evidence URL + the
// admin "clear arrival/departure" path that the quick-action buttons
// can't undo).
//
// Stop_kind is shown as a read-only label -- the backend ignores it on
// update and uses the stored stop_kind for recipient-shape re-validation.
// The conditional recipient inputs mirror the predicates re-exported
// from useLogisticsRouteDetail (same as the add form).
//
// Address validation primitive wired in: the AddressValidationAffordance
// sits directly under the 4 address inputs. Apply-suggestion writes
// back into the edit draft via setEdit. Advisory only -- the diff
// against the stored stop still runs as normal on submit.
import { AddressValidationAffordance } from "@/components/common/AddressValidationAffordance";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./logisticsRoutesShared";
import {
  stopKindAllowsContactRecipient,
  stopKindAllowsOrgRecipient,
  stopKindRequiresContactRecipient,
  stopKindRequiresOrgRecipient,
} from "./useLogisticsRouteDetail";
import type { useLogisticsRouteStopEdits } from "./useLogisticsRouteStopEdits";
import type { LogiStopKind } from "@/lib/logistics";

const STOP_KIND_LABELS: Record<LogiStopKind, string> = {
  plant_origin: "Plant origin",
  dealer_holding: "Dealer holding",
  end_customer: "End customer",
  service: "Service",
  other: "Other",
};

type Props = {
  e: ReturnType<typeof useLogisticsRouteStopEdits>;
};

export function LogisticsRoutesStopEditForm({ e }: Props) {
  if (!e.edit) return null;
  const draft = e.edit;
  const kind = draft.stop_kind;
  return (
    <div className="mt-2 rounded-xl border border-indigo-200 bg-indigo-50/40 dark:border-indigo-800 dark:bg-indigo-950/20 p-3 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
        Edit stop · {STOP_KIND_LABELS[kind] ?? kind}{" "}
        <span className="ml-1 font-normal italic text-gray-500 dark:text-zinc-400">
          (kind is fixed on update)
        </span>
      </div>
      {e.editError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {e.editError}
        </div>
      ) : null}

      {stopKindAllowsOrgRecipient(kind) ? (
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Recipient dealer org (UUID)
          {stopKindRequiresOrgRecipient(kind) ? " — required" : " — optional"}
          <input
            className={`${inputClass} mt-1`}
            value={draft.recipient_org_id_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, recipient_org_id_text: ev.target.value })
            }
            placeholder="organizations.id of the dealer"
          />
        </label>
      ) : null}
      {stopKindAllowsContactRecipient(kind) ? (
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Recipient contact (UUID)
          {stopKindRequiresContactRecipient(kind) ? " — required" : " — optional"}
          <input
            className={`${inputClass} mt-1`}
            value={draft.recipient_contact_id_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, recipient_contact_id_text: ev.target.value })
            }
            placeholder="contact uuid"
          />
        </label>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Street
          <input
            className={`${inputClass} mt-1`}
            value={draft.address_street}
            onChange={(ev) =>
              e.setEdit({ ...draft, address_street: ev.target.value })
            }
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          City
          <input
            className={`${inputClass} mt-1`}
            value={draft.address_city}
            onChange={(ev) =>
              e.setEdit({ ...draft, address_city: ev.target.value })
            }
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          State
          <input
            className={`${inputClass} mt-1`}
            value={draft.address_state}
            onChange={(ev) =>
              e.setEdit({ ...draft, address_state: ev.target.value })
            }
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Postal code
          <input
            className={`${inputClass} mt-1`}
            value={draft.address_postal_code}
            onChange={(ev) =>
              e.setEdit({ ...draft, address_postal_code: ev.target.value })
            }
          />
        </label>
      </div>
      <AddressValidationAffordance
        value={{
          street: draft.address_street,
          city: draft.address_city,
          state: draft.address_state,
          postal_code: draft.address_postal_code,
        }}
        onApply={(n) =>
          e.setEdit({
            ...draft,
            address_street: n.street ?? draft.address_street,
            address_city: n.city ?? draft.address_city,
            address_state: n.state ?? draft.address_state,
            address_postal_code: n.postal_code ?? draft.address_postal_code,
          })
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Window start (ISO; empty clears)
          <input
            className={`${inputClass} mt-1`}
            value={draft.scheduled_window_start_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, scheduled_window_start_text: ev.target.value })
            }
            placeholder="2026-06-15T08:00:00Z"
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Window end (ISO; must be ≥ start)
          <input
            className={`${inputClass} mt-1`}
            value={draft.scheduled_window_end_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, scheduled_window_end_text: ev.target.value })
            }
            placeholder="2026-06-15T12:00:00Z"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Arrived at (ISO; empty clears)
          <input
            className={`${inputClass} mt-1`}
            value={draft.arrived_at_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, arrived_at_text: ev.target.value })
            }
            placeholder="2026-06-15T08:23:00Z"
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Departed at (ISO; must be ≥ arrived when both set)
          <input
            className={`${inputClass} mt-1`}
            value={draft.departed_at_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, departed_at_text: ev.target.value })
            }
            placeholder="2026-06-15T08:45:00Z"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Evidence URL (empty clears)
          <input
            className={`${inputClass} mt-1`}
            value={draft.evidence_url_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, evidence_url_text: ev.target.value })
            }
            placeholder="https://…"
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Evidence completed at (ISO; empty clears)
          <input
            className={`${inputClass} mt-1`}
            value={draft.evidence_completed_at_text}
            onChange={(ev) =>
              e.setEdit({ ...draft, evidence_completed_at_text: ev.target.value })
            }
            placeholder="2026-06-15T08:50:00Z"
          />
        </label>
      </div>

      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Notes
        <input
          className={`${inputClass} mt-1`}
          value={draft.notes_text}
          onChange={(ev) =>
            e.setEdit({ ...draft, notes_text: ev.target.value })
          }
        />
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          className={primaryBtnClass}
          onClick={() => void e.submitEdit()}
          disabled={e.editBusy}
        >
          {e.editBusy ? "Saving…" : "Save changes"}
        </button>
        <button type="button" className={ghostBtnClass} onClick={e.cancelEdit}>
          Cancel
        </button>
      </div>
    </div>
  );
}
